import { Check, Link2, X } from "lucide-react"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { searchUsers } from "@/features/profile/services/user.service"
import { useSendShare } from "@/hooks/useShare"
import { getAssetUrl } from "@/utils/assets"

interface ShareModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  shareUrl: string
  shareText?: string
  shareType?: "ritual" | "community" | "profile"
  displayPath?: string
  username?: string
}

interface SelectedUser {
  id: string
  name: string | null
  username: string | null
  profilePictureUrl: string | null
  profileMedia?: Array<{
    isCover: boolean
    upload?: {
      id: string
      filePath: string
    }
  }>
}

interface SearchResult {
  id: string
  name: string | null
  username: string | null
  profilePictureUrl: string | null
  profileMedia?: Array<{
    isCover: boolean
    upload?: {
      id: string
      filePath: string
    }
  }>
}

const getUserAvatar = (user: SelectedUser | SearchResult): string | null => {
  const coverMedia = user.profileMedia?.find(media => media.isCover && media.upload)
  return coverMedia?.upload?.filePath || null
}

const getUserInitials = (user: SelectedUser | SearchResult): string => {
  if (user.name) {
    const parts = user.name.split(" ")
    return parts.length >= 2
      ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
      : parts[0].slice(0, 2).toUpperCase()
  }
  return user.username?.slice(0, 2).toUpperCase() || "??"
}

const socialPlatforms = [
  {
    name: "Instagram",
    icon: getAssetUrl("instagram.svg"),
    color:
      "hover:bg-gradient-to-br hover:from-[#833AB4]/20 hover:via-[#FD1D1D]/20 hover:to-[#F77737]/20 hover:border-[#E1306C]",
  },
  {
    name: "Facebook",
    icon: getAssetUrl("facebook.svg"),
    color: "hover:bg-[#1877F2]/20 hover:border-[#1877F2]",
    getUrl: (url: string, text: string) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`,
  },
  {
    name: "TikTok",
    icon: getAssetUrl("ticktok.svg"),
    color: "hover:bg-[#00f2ea]/20 hover:border-[#00f2ea] bg-[#F2F0EB0D]",
    getUrl: (_url: string, text: string) =>
      `https://www.tiktok.com/search?q=${encodeURIComponent(text)}`,
  },
  {
    name: "Threads",
    icon: getAssetUrl("thread.svg"),
    color: "hover:bg-black/20 hover:border-black",
    getUrl: (url: string, text: string) =>
      `https://www.threads.net/intent/post?text=${encodeURIComponent(`${text} ${url}`)}`,
  },
  {
    name: "Twitter",
    icon: getAssetUrl("twitter.svg"),
    color: "hover:bg-[#1DA1F2]/20 hover:border-[#1DA1F2] bg-[#F2F0EB0D]",
    getUrl: (url: string, text: string) =>
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
  },
  {
    name: "LinkedIn",
    icon: getAssetUrl("linkedin.svg"),
    color: "hover:bg-[#0A66C2]/20 hover:border-[#0A66C2] bg-[#F2F0EB0D]",
    getUrl: (url: string) =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  },
  {
    name: "Pinterest",
    icon: getAssetUrl("pinterest.svg"),
    color: "hover:bg-[#BD081C]/20 hover:border-[#BD081C] bg-[#F2F0EB0D]",
    getUrl: (url: string, text: string) =>
      `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&description=${encodeURIComponent(text)}`,
  },
  {
    name: "WhatsApp",
    icon: getAssetUrl("whatsapp.svg"),
    color: "hover:bg-[#25D366]/20 hover:border-[#25D366] bg-[#F2F0EB0D]",
    getUrl: (url: string, text: string) =>
      `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`,
  },
]

function getShareBaseUrl(): string {
  const appUrl = import.meta.env.VITE_APP_URL
  if (appUrl) {
    const host = appUrl.startsWith("http") ? new URL(appUrl).origin : `https://${appUrl}`
    return host
  }
  const origin = typeof window !== "undefined" ? window.location.origin : ""
  return origin.replace(/^http:/, "https:")
}

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

export function ShareModal({
  open,
  onOpenChange,
  shareUrl,
  shareText = "",
  shareType = "profile",
}: ShareModalProps) {
  const [copied, setCopied] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>([])
  const [message, setMessage] = useState("")
  const [isSearching, setIsSearching] = useState(false)

  const baseUrl = getShareBaseUrl()

  const resolvedShareUrl = shareUrl.startsWith("http")
    ? shareUrl.replace(/^http:/, "http:")
    : `${baseUrl}${shareUrl}`

  const debouncedSearchQuery = useDebounce(searchQuery, 300)

  const { mutate: sendShare, isPending: isSending } = useSendShare()

  useEffect(() => {
    const searchForUsers = async () => {
      if (!debouncedSearchQuery || debouncedSearchQuery.length < 2) {
        setSearchResults([])
        return
      }

      setIsSearching(true)
      try {
        const response = await searchUsers({ search: debouncedSearchQuery, limit: 10 })
        const filteredResults = response.users.filter(
          user => !selectedUsers.some(selected => selected.id === user.id),
        )
        setSearchResults(filteredResults)
      } catch {
        setSearchResults([])
      } finally {
        setIsSearching(false)
      }
    }

    searchForUsers()
  }, [debouncedSearchQuery, selectedUsers])

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(resolvedShareUrl)
      setCopied(true)
      toast.success("Link copied to clipboard!")
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error("Failed to copy link. Please try again.")
    }
  }

  const handleShare = (platform: (typeof socialPlatforms)[0]) => {
    if (platform.name === "Instagram") {
      window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer")
      return
    }

    if (platform.getUrl) {
      window.open(
        platform.getUrl(resolvedShareUrl, shareText),
        "_blank",
        "width=600,height=400,noopener,noreferrer",
      )
    }
  }

  const handleSelectUser = (user: SearchResult) => {
    if (selectedUsers.length >= 5) {
      toast.error("Maximum 5 users can be selected")
      return
    }
    if (!selectedUsers.some(selected => selected.id === user.id)) {
      setSelectedUsers([...selectedUsers, user])
    }
    setSearchQuery("")
    setSearchResults([])
  }

  const handleRemoveUser = (userId: string) => {
    setSelectedUsers(selectedUsers.filter(user => user.id !== userId))
  }

  const handleSendShare = () => {
    if (selectedUsers.length === 0) {
      toast.error("Please select at least one user")
      return
    }

    sendShare(
      {
        userIds: selectedUsers.map(user => user.id),
        message,
        shareUrl: resolvedShareUrl,
        shareType,
      },
      {
        onSuccess: () => {
          setSelectedUsers([])
          setMessage("")
          onOpenChange(false)
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-175 flex flex-col gap-6.25 pt-7 pb-5 max-h-[90vh] overflow-y-auto">
        <DialogHeader className="text-center flex flex-col gap-5 items-center">
          <DialogTitle className="text-xl font-semibold leading-normal text-[#F2F0EBE6]">
            Share
          </DialogTitle>
          <DialogDescription className="text-sm leading-normal text-[#F2F0EBE6]">
            Share this link to social media
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center justify-center gap-3.75 px-2">
          {socialPlatforms.map(platform => (
            <button
              key={platform.name}
              type="button"
              onClick={() => handleShare(platform)}
              className={`flex items-center justify-center rounded-full w-12.5 h-12.5 transition-all duration-200 ${platform.name === "Instagram" || platform.name === "Facebook" || platform.name === "Threads" ? "p-0" : "border border-[#F2F0EB26] "} ${platform.color}`}
              aria-label={`Share to ${platform.name}`}
            >
              <img
                src={platform.icon}
                alt={platform.name}
                className={`${platform.name === "Instagram" || platform.name === "Facebook" || platform.name === "Threads" ? "w-full h-full" : "w-5 h-5"}`}
              />
            </button>
          ))}
          <button
            type="button"
            onClick={handleCopyLink}
            className={`flex items-center justify-center rounded-full w-12.5 h-12.5 transition-all duration-200 bg-[#F2F0EB0D] border border-[#F2F0EB26] hover:bg-brand-olive/20 hover:border-brand-olive ${copied ? "bg-brand-olive/20 border-brand-olive" : ""}`}
            aria-label="Copy link"
          >
            {copied ? (
              <Check className="w-5 h-5 text-brand-olive" />
            ) : (
              <Link2 className="w-5 h-5 text-[#F2F0EBE6]" />
            )}
          </button>
        </div>

        <div>
          <span className="text-sm text-[#F2F0EBE6] leading-normal block text-center mb-4">
            or send it directly to friends
          </span>

          <div className="flex flex-col gap-6.25">
            <div className="flex flex-col gap-5">
              <div className="bg-[#FFFFFF0D] border border-[#FFFFFF1A] rounded-[10px] flex items-center gap-2 px-4 py-2.5 focus-within:border-brand-olive/50 transition-colors">
                <div className="flex items-center gap-2 overflow-x-auto flex-1 min-w-0">
                  {selectedUsers.map(user => {
                    const avatarUrl = getUserAvatar(user)
                    const initials = getUserInitials(user)
                    return (
                      <div
                        key={user.id}
                        className="flex items-center gap-2 bg-[#FFFFFF0D] border border-[#FFFFFF1A] rounded-full px-3 py-1.5 shrink-0"
                      >
                        {avatarUrl ? (
                          <img
                            src={avatarUrl}
                            alt={user.name || user.username || ""}
                            className="w-5 h-5 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-[#FFFFFF1A] flex items-center justify-center text-[10px] font-medium text-[#F2F0EB]/70">
                            {initials}
                          </div>
                        )}
                        <span className="text-xs text-[#F2F0EBE6]">
                          {user.name || user.username}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveUser(user.id)}
                          className="text-[#F2F0EB]/50 hover:text-[#F2F0EB] transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )
                  })}
                  <input
                    type="text"
                    placeholder={
                      selectedUsers.length === 0
                        ? "Search friends by name or username"
                        : "Add more..."
                    }
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full min-w-[120px] bg-transparent text-sm leading-6.25 text-[#F2F0EBE6] placeholder:text-[#F2F0EB]/50 focus:outline-none"
                  />
                </div>
                {isSearching ? (
                  <div className="flex items-center shrink-0">
                    <div className="w-4 h-4 border-2 border-[#F2F0EB]/30 border-t-[#F2F0EB] rounded-full animate-spin" />
                  </div>
                ) : (
                  <span className="text-xs text-[#F2F0EB]/50 whitespace-nowrap shrink-0">
                    {selectedUsers.length}/5
                  </span>
                )}
              </div>

              {searchResults.length > 0 && (
                <div className="bg-[#1a1a1a] border border-[#FFFFFF1A] rounded-[10px] max-h-48 overflow-y-auto">
                  {searchResults.map(user => {
                    const avatarUrl = getUserAvatar(user)
                    const initials = getUserInitials(user)
                    return (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => handleSelectUser(user)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#FFFFFF0D] transition-colors text-left"
                      >
                        {avatarUrl ? (
                          <img
                            src={avatarUrl}
                            alt={user.name || user.username || ""}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-[#FFFFFF1A] flex items-center justify-center text-xs font-medium text-[#F2F0EB]/70">
                            {initials}
                          </div>
                        )}
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm text-[#F2F0EBE6] truncate">
                            {user.name || user.username}
                          </span>
                          {user.username && (
                            <span className="text-xs text-[#F2F0EB]/50 truncate">
                              @{user.username}
                            </span>
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}

              <textarea
                placeholder="Add message"
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={4}
                className="w-full p-5 bg-[#FFFFFF0D] border border-[#FFFFFF1A] rounded-[10px] text-sm text-[#F2F0EBE6] placeholder:text-[#F2F0EB]/50 focus:outline-none focus:border-brand-olive/50 transition-colors resize-none"
              />
            </div>

            <div className="flex gap-2.5 justify-end">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="md:w-[205px] flex-1 md:flex-none w-full py-3 text-sm font-semibold uppercase border-[#F2F0EB] text-[#F2F0EB]"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSendShare}
                disabled={selectedUsers.length === 0 || isSending}
                isLoading={isSending}
                className="md:w-[205px] flex-1 md:flex-none w-full py-3 text-sm font-semibold uppercase gap-2"
              >
                Send
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
